import {CurrencyPipe, PercentPipe} from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  FeeSnapshot,
  FeeSnapshotItem,
  FeeType,
  FlowDirection,
} from '@gatewee/types/fee';
import {PaymentMethod} from '@gatewee/types/payment';
import {TranslateService} from '@ngx-translate/core';
import {TuiDialogService} from '@taiga-ui/core';

export interface FeeEntity {
  id: number;
  name: string;
  document: string;
  representative?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}

/** Installment tier: PADRÃO (catch-all) or 1–24 */
export type CcInstallmentTier = 'default' | number;

export interface FeesSettingsActions {
  onSendEmail?: (entity: FeeEntity) => void;
  onSendWhatsapp?: (entity: FeeEntity) => void;
  onSubmit?: (fees: FeeSnapshotItem[]) => void;
}

@Component({
  selector: 'gatewee-fees-settings',
  templateUrl: './gatewee-fees-settings.component.html',
  styleUrls: ['./gatewee-fees-settings.component.css'],
  standalone: false,
  providers: [CurrencyPipe, PercentPipe],
})
export class GateweeFeesSettingsComponent implements OnInit, OnChanges {
  @Input() entity!: FeeEntity;
  @Input() fees!: FeeSnapshot;
  @Input() actions?: FeesSettingsActions;
  @Input() enabledPaymentMethods: Record<string, boolean> = {};
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.fees.title';
  @Input() titlePath = 'features.fees.title_path';
  @Input() disableContentPage = false;
  @Input() feeFormTemplate?: TemplateRef<unknown>;
  @Input() scope: 'domain' | 'recipient' = 'domain';
  @Input() defaultFees: FeeSnapshotItem[] | null = null;

  readonly FlowDirection = FlowDirection;
  readonly PaymentMethod = PaymentMethod;

  /**
   * Credit card matrix only supports the default (brandless) column.
   * Branded rows in the API are ignored by this UI until migrated.
   */
  readonly ccBrandless = null;

  readonly selectableIncomeMethods: PaymentMethod[] = [
    PaymentMethod.PIX,
    PaymentMethod.SLIPIX,
    PaymentMethod.BANK_SLIP,
    PaymentMethod.BITCOIN,
  ];

  feeCardColors = [
    'gw-bg-info',
    'gw-bg-success',
    'gw-bg-secondary',
    'gw-bg-primary',
    'gw-bg-danger',
  ];

  editMode = false;
  feesForm!: FormGroup;
  feeTypeOptions: FeeType[] = ['amount', 'rate'];

  /** Show installment rows 13–24 in the credit card matrix */
  showInstallments13to24 = false;

  /** User-added installment tiers (e.g. after “Add installment”) */
  private manualInstallmentTiers = new Set<number>();

  /**
   * Installment rows removed in the current edit session (still in @Input fees until save).
   * Without this, computeCcMatrixRows would keep re-adding tiers from the snapshot.
   */
  private removedInstallmentTiers = new Set<number>();

  /**
   * Cached lists for template *ngFor — avoids new array refs every CD cycle
   * (which destroyed DOM and reset page scroll).
   */
  ccMatrixRows: CcInstallmentTier[] = [];
  displayEditableFees: FeeSnapshotItem[] = [];
  unconfiguredIncomeMethodsList: PaymentMethod[] = [];

  @ViewChild('removeFeesDialog', {read: TemplateRef})
  removeFeesDialog!: TemplateRef<unknown>;

  constructor(
    private currencyPipe: CurrencyPipe,
    private percentPipe: PercentPipe,
    private fb: FormBuilder,
    private translate: TranslateService,
    private dialogService: TuiDialogService,
  ) {}

  ngOnInit(): void {
    if (!this.fees) {
      console.error('GateweeFeesSettings: entity and fees are required inputs');
      console.error('Entity:', this.entity);
      console.error('Fees:', this.fees);
    } else {
      this.initializeForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['fees'] || !this.fees) {
      return;
    }
    if (changes['fees'].firstChange) {
      return;
    }
    this.editMode = false;
    this.manualInstallmentTiers.clear();
    this.removedInstallmentTiers.clear();
    this.showInstallments13to24 = false;
    this.initializeForm();
  }

  normalizeBrand(brand?: string | null): string | null {
    if (brand === null || brand === undefined || brand === '') {
      return null;
    }
    return brand;
  }

  ccFormKey(tier: CcInstallmentTier, brand: string | null): string {
    const inst = tier === 'default' ? 'def' : String(tier);
    const br = brand === null || brand === undefined ? 'any' : brand;
    return `cc_${inst}_${br}`;
  }

  parseCcFormKey(
    key: string,
  ): {tier: CcInstallmentTier; brand: string | null} | null {
    if (!key.startsWith('cc_')) {
      return null;
    }
    const rest = key.slice(3);
    const underscore = rest.indexOf('_');
    if (underscore < 0) {
      return null;
    }
    const instPart = rest.slice(0, underscore);
    const brandPart = rest.slice(underscore + 1);
    const tier: CcInstallmentTier =
      instPart === 'def' ? 'default' : Number(instPart);
    const brand = brandPart === 'any' ? null : brandPart;
    return {tier, brand};
  }

  tierInstallmentsValue(tier: CcInstallmentTier): number | null {
    return tier === 'default' ? null : tier;
  }

  findCcFeeInList(
    list: FeeSnapshotItem[] | undefined,
    tier: CcInstallmentTier,
    brand: string | null,
  ): FeeSnapshotItem | undefined {
    if (!list) {
      return undefined;
    }
    const inst = this.tierInstallmentsValue(tier);
    return list.find(
      f =>
        f.paymentMethod === PaymentMethod.CREDIT_CARD &&
        (f.operation ?? FlowDirection.INCOME) === FlowDirection.INCOME &&
        (f.installments ?? null) === inst &&
        this.normalizeBrand(f.brand) === brand,
    );
  }

  /** Domain CC row for this tier (brandless only). */
  private findDomainCcFeeExact(
    tier: CcInstallmentTier,
  ): FeeSnapshotItem | undefined {
    return this.findCcFeeInList(this.fees.domain, tier, null);
  }

  /**
   * Domain base shown to recipients: tier-specific brandless row, else brandless "default"
   * row when installments are unset (catch-all).
   */
  private resolveRecipientCcDomainBase(
    tier: CcInstallmentTier,
  ): FeeSnapshotItem | undefined {
    const exact = this.findDomainCcFeeExact(tier);
    if (exact) {
      return exact;
    }
    if (tier !== 'default') {
      return this.findDomainCcFeeExact('default');
    }
    return undefined;
  }

  /**
   * Recipient may edit brandless CC markup for a tier if the domain defines that tier
   * or has a brandless default (installments unset) row.
   */
  private domainCcAllowsRecipientCell(tier: CcInstallmentTier): boolean {
    if (this.findDomainCcFeeExact(tier)) {
      return true;
    }
    const domain = this.fees.domain ?? [];
    const hasDefaultBrandlessCc = domain.some(
      f =>
        f.paymentMethod === PaymentMethod.CREDIT_CARD &&
        (f.operation ?? FlowDirection.INCOME) === FlowDirection.INCOME &&
        this.normalizeBrand(f.brand) === null &&
        (f.installments ?? null) === null,
    );
    return hasDefaultBrandlessCc && tier !== 'default';
  }

  collectDistinctCcCellsFromSnapshot(): Array<{
    tier: CcInstallmentTier;
    brand: string | null;
  }> {
    const map = new Map<
      string,
      {tier: CcInstallmentTier; brand: string | null}
    >();
    const add = (f: FeeSnapshotItem) => {
      if (f.paymentMethod !== PaymentMethod.CREDIT_CARD) {
        return;
      }
      if ((f.operation ?? FlowDirection.INCOME) !== FlowDirection.INCOME) {
        return;
      }
      const tier: CcInstallmentTier =
        f.installments === null || f.installments === undefined
          ? 'default'
          : f.installments;
      if (typeof tier === 'number' && (tier < 1 || tier > 24)) {
        return;
      }
      const brand = this.normalizeBrand(f.brand);
      if (brand !== null) {
        return;
      }
      const key = this.ccFormKey(
        f.installments === null || f.installments === undefined
          ? 'default'
          : f.installments,
        null,
      );
      map.set(key, {
        tier:
          f.installments === null || f.installments === undefined
            ? 'default'
            : f.installments,
        brand: null,
      });
    };
    this.fees.domain?.forEach(add);
    if (this.scope === 'recipient') {
      this.fees.recipient?.forEach(add);
    }
    return [...map.values()];
  }

  private computeCcMatrixRows(): CcInstallmentTier[] {
    const set = new Set<string>();

    const addTier = (t: CcInstallmentTier) => {
      if (typeof t === 'number' && this.removedInstallmentTiers.has(t)) {
        return;
      }
      set.add(t === 'default' ? 'default' : String(t));
    };

    addTier('default');
    if (this.showInstallments13to24) {
      for (let i = 13; i <= 24; i++) {
        addTier(i);
      }
    }
    this.manualInstallmentTiers.forEach(n => addTier(n));
    this.collectDistinctCcCellsFromSnapshot().forEach(c => addTier(c.tier));

    const order = (t: CcInstallmentTier): number =>
      t === 'default' ? 0 : (t as number);

    return Array.from(set)
      .map(s =>
        s === 'default' ? 'default' : (Number(s) as CcInstallmentTier),
      )
      .sort((a, b) => order(a) - order(b));
  }

  /** Refresh cached arrays used in the template (call after any matrix / fee-list state change). */
  private syncTemplateLists(): void {
    this.ccMatrixRows = this.computeCcMatrixRows();
    this.displayEditableFees = this.getEditableFees();
    this.unconfiguredIncomeMethodsList = this.getUnconfiguredIncomeMethods();
  }

  toggleInstallments13to24(): void {
    this.showInstallments13to24 = !this.showInstallments13to24;
    this.syncTemplateLists();
  }

  trackByCcTier(_index: number, tier: CcInstallmentTier): string | number {
    return tier === 'default' ? 'default' : tier;
  }

  trackByEditableFee(_index: number, fee: FeeSnapshotItem): string {
    return `${fee.operation ?? 'income'}_${fee.paymentMethod ?? 'none'}_${
      fee.installments ?? ''
    }_${fee.brand ?? ''}`;
  }

  trackByPaymentMethodOption(_index: number, m: PaymentMethod): string {
    return m;
  }

  formatTierLabel(tier: CcInstallmentTier): string {
    return tier === 'default'
      ? this.translate.instant('features.fees.default')
      : `${tier}x`;
  }

  hasCcFormControl(tier: CcInstallmentTier): boolean {
    const key = this.ccFormKey(tier, this.ccBrandless);
    return !!this.feesForm?.get(key);
  }

  getCcFormGroup(tier: CcInstallmentTier): FormGroup | null {
    const key = this.ccFormKey(tier, this.ccBrandless);
    return (this.feesForm?.get(key) as FormGroup) ?? null;
  }

  addCcCell(tier: CcInstallmentTier): void {
    this.enterEditModeFromAction();
    const key = this.ccFormKey(tier, this.ccBrandless);
    if (this.feesForm.contains(key)) {
      return;
    }
    if (typeof tier === 'number') {
      this.removedInstallmentTiers.delete(tier);
    }
    const domainF =
      this.scope === 'domain'
        ? this.findDomainCcFeeExact(tier)
        : this.resolveRecipientCcDomainBase(tier);
    const recipientF = this.findCcFeeInList(
      this.fees.recipient ?? [],
      tier,
      this.ccBrandless,
    );
    this.feesForm.addControl(
      key,
      this.buildCcFormGroup(domainF, recipientF, tier, this.ccBrandless),
    );
    if (typeof tier === 'number') {
      this.manualInstallmentTiers.add(tier);
    }
    this.syncTemplateLists();
  }

  removeCcCell(tier: CcInstallmentTier): void {
    const key = this.ccFormKey(tier, this.ccBrandless);
    if (this.feesForm.contains(key)) {
      this.feesForm.removeControl(key);
    }
  }

  /** Add next missing installment row (1–24), defaulting to 2x when possible. */
  addCreditCardInstallmentRow(): void {
    const rows = this.ccMatrixRows.filter((r): r is number => r !== 'default');
    const preferred = 2;
    if (!rows.includes(preferred)) {
      this.removedInstallmentTiers.delete(preferred);
      this.manualInstallmentTiers.add(preferred);
      this.addCcCell(preferred);
      return;
    }
    for (let n = 1; n <= 24; n++) {
      if (!rows.includes(n)) {
        this.removedInstallmentTiers.delete(n);
        this.manualInstallmentTiers.add(n);
        this.addCcCell(n);
        return;
      }
    }
    this.addCcCell('default');
  }

  removeCcInstallmentRow(tier: CcInstallmentTier): void {
    this.removeCcCell(tier);
    if (typeof tier === 'number') {
      this.manualInstallmentTiers.delete(tier);
      this.removedInstallmentTiers.add(tier);
    }
    this.syncTemplateLists();
  }

  private buildCcFormGroup(
    domainF: FeeSnapshotItem | undefined,
    recipientF: FeeSnapshotItem | undefined,
    tier: CcInstallmentTier,
    brand: string | null,
  ): FormGroup {
    const domainType = domainF?.type || 'rate';
    const domainValue = this.getFeeValue(domainF ?? {type: 'rate', value: 0});

    if (this.scope === 'domain') {
      return this.fb.group({
        type: [domainF?.type || 'rate', Validators.required],
        value: [
          domainValue,
          [Validators.required, (c: FormControl) => this.validateFeeValue(c)],
        ],
        paymentMethod: [PaymentMethod.CREDIT_CARD],
        operation: [FlowDirection.INCOME],
        installments: [this.tierInstallmentsValue(tier)],
        brand: [brand],
      });
    }

    const recipientType = recipientF?.type || domainType || 'rate';
    const recipientValue = recipientF ? this.getFeeValue(recipientF) : 0;

    return this.fb.group({
      type: [recipientType, Validators.required],
      value: [
        recipientValue,
        [Validators.required, (c: FormControl) => this.validateFeeValue(c)],
      ],
      paymentMethod: [PaymentMethod.CREDIT_CARD],
      operation: [FlowDirection.INCOME],
      installments: [this.tierInstallmentsValue(tier)],
      brand: [brand],
      _domainValue: [domainValue],
      _domainType: [domainType],
    });
  }

  initializeForm(): void {
    const editableFees = this.getEditableFees();
    const formControls: Record<string, FormGroup> = {};

    editableFees.forEach(fee => {
      const key = this.getFeeFormKey(fee);
      formControls[key] = this.fb.group({
        type: [fee.type || 'amount', Validators.required],
        value: [
          this.getFeeValue(fee),
          [
            Validators.required,
            (control: FormControl) => this.validateFeeValue(control),
          ],
        ],
        paymentMethod: [fee.paymentMethod],
        operation: [fee.operation ?? FlowDirection.INCOME],
      });
    });

    for (const cell of this.collectDistinctCcCellsFromSnapshot()) {
      const key = this.ccFormKey(cell.tier, this.ccBrandless);
      const domainFExact = this.findDomainCcFeeExact(cell.tier);
      const domainF =
        this.scope === 'recipient'
          ? this.resolveRecipientCcDomainBase(cell.tier)
          : domainFExact;
      const recipientF = this.findCcFeeInList(
        this.fees.recipient ?? [],
        cell.tier,
        this.ccBrandless,
      );
      formControls[key] = this.buildCcFormGroup(
        domainF,
        recipientF,
        cell.tier,
        this.ccBrandless,
      );
    }

    this.feesForm = this.fb.group(formControls);
    this.syncTemplateLists();
  }

  getFeeFormKey(fee: FeeSnapshotItem): string {
    if (fee.operation === FlowDirection.CASH_OUT) {
      return 'cash_out';
    }
    return fee.paymentMethod || 'unknown';
  }

  validateFeeValue(control: AbstractControl): ValidationErrors | null {
    const parent = control?.parent;
    if (!parent) {
      return null;
    }

    const type = parent.get('type')?.value;
    const value = control.value;

    if (type === 'rate' && value > 100) {
      return {maxRate: true};
    }

    if (value < 0) {
      return {minValue: true};
    }

    return null;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) {
      this.removedInstallmentTiers.clear();
      this.initializeForm();
    } else {
      this.manualInstallmentTiers.clear();
      this.removedInstallmentTiers.clear();
      this.initializeForm();
    }
  }

  private enterEditModeFromAction(): void {
    if (!this.editMode) {
      this.editMode = true;
      this.initializeForm();
    }
  }

  cancelEdit(): void {
    this.editMode = false;
    this.manualInstallmentTiers.clear();
    this.removedInstallmentTiers.clear();
    this.initializeForm();
  }

  saveFees(): void {
    if (!this.feesForm.valid) {
      return;
    }

    const formValues = this.feesForm.value;
    const fees: FeeSnapshotItem[] = [];

    Object.keys(formValues).forEach(key => {
      const feeControl = formValues[key];
      if (key.startsWith('cc_')) {
        const parsed = this.parseCcFormKey(key);
        if (!parsed) {
          return;
        }
        const fee: FeeSnapshotItem = {
          type: feeControl.type,
          value:
            typeof feeControl.value === 'string'
              ? Number(feeControl.value)
              : feeControl.value,
          paymentMethod: PaymentMethod.CREDIT_CARD,
          operation: FlowDirection.INCOME,
        };
        if (parsed.tier !== 'default') {
          fee.installments = parsed.tier as number;
        } else {
          fee.installments = undefined;
        }
        if (parsed.brand !== null && parsed.brand !== undefined) {
          fee.brand = parsed.brand;
        }
        fees.push(fee);
        return;
      }

      const op = feeControl.operation ?? FlowDirection.INCOME;
      if (op === FlowDirection.CASH_OUT) {
        fees.push({
          type: feeControl.type,
          value:
            typeof feeControl.value === 'string'
              ? Number(feeControl.value)
              : feeControl.value,
          operation: FlowDirection.CASH_OUT,
        });
      } else {
        fees.push({
          type: feeControl.type,
          value:
            typeof feeControl.value === 'string'
              ? Number(feeControl.value)
              : feeControl.value,
          paymentMethod: feeControl.paymentMethod,
          operation: op,
        });
      }
    });

    this.handleSubmit(fees);
    this.editMode = false;
    this.manualInstallmentTiers.clear();
    this.removedInstallmentTiers.clear();
  }

  getFeeValueControl(fee: FeeSnapshotItem): FormControl | null {
    const group = this.getFeeFormControl(fee);
    if (!group) {
      return null;
    }
    return group.get('value') as FormControl;
  }

  getFeeTypeControl(fee: FeeSnapshotItem): FormControl | null {
    const group = this.getFeeFormControl(fee);
    if (!group) {
      return null;
    }
    return group.get('type') as FormControl;
  }

  getFeeFormControl(fee: FeeSnapshotItem): FormGroup | null {
    const key = this.getFeeFormKey(fee);
    return (this.feesForm?.get(key) as FormGroup) || null;
  }

  getDomainFees(): FeeSnapshotItem[] {
    return (
      this.fees?.domain?.filter(fee => fee.paymentMethod !== 'credit_card') ||
      []
    );
  }

  getFeeInfo(method: PaymentMethod): {value: number; type: FeeType} {
    if (this.scope === 'domain') {
      return {
        value: 0,
        type: 'amount',
      };
    }
    const recipientFee = this.fees.recipient?.find(
      e => e.paymentMethod === method,
    );
    const feeValue = recipientFee?.value;
    return {
      value:
        typeof feeValue === 'string' ? Number(feeValue) || 0 : feeValue || 0,
      type: recipientFee?.type || 'amount',
    };
  }

  getEditableFees(): FeeSnapshotItem[] {
    if (this.scope === 'domain') {
      return this.getDomainFees();
    }
    const domainFees = this.getDomainFees();
    return domainFees.map(domainFee => {
      const op = domainFee.operation ?? FlowDirection.INCOME;
      const match = this.fees?.recipient?.find(
        r =>
          r.paymentMethod === domainFee.paymentMethod &&
          (r.operation ?? FlowDirection.INCOME) === op,
      );
      if (match) {
        return match;
      }
      return {
        type: 'amount' as FeeType,
        value: 0,
        paymentMethod: domainFee.paymentMethod,
        operation: op,
      };
    });
  }

  getDomainFeeForMethod(method: PaymentMethod): FeeSnapshotItem | undefined {
    return this.fees?.domain?.find(
      fee =>
        fee.paymentMethod === method && fee.paymentMethod !== 'credit_card',
    );
  }

  getDomainFeeValue(method: PaymentMethod): number {
    const domainFee = this.getDomainFeeForMethod(method);
    if (!domainFee) {
      return 0;
    }
    const val = domainFee.value;
    return typeof val === 'string' ? Number(val) || 0 : val || 0;
  }

  getFeeValue(fee: FeeSnapshotItem): number {
    const val = fee.value;
    return typeof val === 'string' ? Number(val) || 0 : val || 0;
  }

  isDomainFeeEditable(): boolean {
    return this.scope === 'domain';
  }

  formatFeeByType(type: FeeType, value: number | string): string {
    const numValue = typeof value === 'string' ? Number(value) : value;

    if (type === 'rate') {
      return this.percentPipe.transform(numValue / 100, '1.2-2') || '';
    }
    return (
      this.currencyPipe.transform(
        numValue,
        'BRL',
        'symbol',
        '1.2-2',
        'pt-BR',
      ) || ''
    );
  }

  /** When base and markup use different fee types, show both formatted values. */
  private formatTotalWhenMixedTypes(
    baseType: FeeType,
    baseValue: number,
    markupType: FeeType,
    markupValue: number,
  ): string {
    return `${this.formatFeeByType(
      baseType,
      baseValue,
    )} + ${this.formatFeeByType(markupType, markupValue)}`;
  }

  /** Recipient markup only (standard payment methods, not combined with domain). */
  getRecipientMarkupDisplay(fee: FeeSnapshotItem): string {
    return this.formatFeeByType(fee.type, this.getFeeValue(fee));
  }

  getTotalFeeDisplay(fee: FeeSnapshotItem): string {
    if (this.scope === 'domain') {
      const numValue = this.getFeeValue(fee);
      return this.formatFeeByType(fee.type, numValue);
    }
    if (!fee.paymentMethod) {
      const numValue = this.getFeeValue(fee);
      return this.formatFeeByType(fee.type, numValue);
    }
    const domainFee = this.getDomainFeeForMethod(fee.paymentMethod);
    if (!domainFee) {
      const numValue = this.getFeeValue(fee);
      return this.formatFeeByType(fee.type, numValue);
    }
    const domainValue = this.getDomainFeeValue(fee.paymentMethod);
    const recipientValue = this.getFeeValue(fee);

    if (domainFee.type === fee.type) {
      const total = domainValue + recipientValue;
      return this.formatFeeByType(fee.type, total);
    }
    return this.formatTotalWhenMixedTypes(
      domainFee.type,
      domainValue,
      fee.type,
      recipientValue,
    );
  }

  getFeeCardColor(index: number, operation?: FlowDirection): string {
    if (operation === FlowDirection.CASH_OUT) {
      return 'gw-bg-status-negative';
    }
    return this.feeCardColors[index % this.feeCardColors.length];
  }

  getPaymentMethodLabel(method: PaymentMethod): string {
    return `paymentMethods.${method}`;
  }

  getOperationLabel(operation?: FlowDirection): string {
    if (operation === FlowDirection.CASH_OUT) {
      return 'features.fees.operations.cashOut';
    }
    return 'features.fees.operations.income';
  }

  handleSendEmail(): void {
    if (this.actions?.onSendEmail && this.entity) {
      this.actions.onSendEmail(this.entity);
    }
  }

  handleSendWhatsapp(): void {
    if (this.actions?.onSendWhatsapp && this.entity) {
      this.actions.onSendWhatsapp(this.entity);
    }
  }

  handleSubmit(fees: FeeSnapshotItem[]): void {
    if (this.actions?.onSubmit) {
      const feesToSubmit =
        this.scope === 'domain'
          ? fees
          : fees.filter(fee => this.isRecipientScopedSubmitFee(fee));
      this.actions.onSubmit(feesToSubmit);
    }
  }

  canConfigureCcCell(tier: CcInstallmentTier): boolean {
    if (this.scope === 'domain') {
      return true;
    }
    return this.domainCcAllowsRecipientCell(tier);
  }

  getCcTypeControl(tier: CcInstallmentTier): FormControl | null {
    return (this.getCcFormGroup(tier)?.get('type') as FormControl) ?? null;
  }

  getCcValueControl(tier: CcInstallmentTier): FormControl | null {
    return (this.getCcFormGroup(tier)?.get('value') as FormControl) ?? null;
  }

  startConfigureCreditCard(): void {
    this.addCcCell('default');
  }

  onAddIncomeMethodSelect(select: HTMLSelectElement): void {
    const v = select.value as PaymentMethod;
    if (v) {
      this.addIncomePaymentMethod(v);
      select.value = '';
    }
  }

  private isRecipientScopedSubmitFee(fee: FeeSnapshotItem): boolean {
    const op = fee.operation ?? FlowDirection.INCOME;
    const domain = this.fees.domain ?? [];
    if (op === FlowDirection.CASH_OUT) {
      return domain.some(d => d.operation === FlowDirection.CASH_OUT);
    }
    if (!fee.paymentMethod) {
      return false;
    }
    if (fee.paymentMethod === PaymentMethod.CREDIT_CARD) {
      if (this.normalizeBrand(fee.brand) !== null) {
        return false;
      }
      const tier: CcInstallmentTier =
        fee.installments === null || fee.installments === undefined
          ? 'default'
          : fee.installments;
      return this.domainCcAllowsRecipientCell(tier);
    }
    return domain.some(
      d =>
        d.paymentMethod === fee.paymentMethod &&
        (d.operation ?? FlowDirection.INCOME) === op,
    );
  }

  removeAllFees(): void {
    const title = this.translate.instant(
      'features.fees.removeAllFeesConfirmTitle',
    );
    const message = this.translate.instant(
      'features.fees.removeAllFeesConfirmMessage',
    );

    this.dialogService
      .open<boolean>(this.removeFeesDialog, {
        label: title,
        size: 's',
        closeable: true,
        dismissible: true,
        data: {message},
      })
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.handleSubmit([]);
          }
        },
      });
  }

  showCreditCardSection(): boolean {
    return this.enabledPaymentMethods?.['credit_card'] === true && !!this.fees;
  }

  creditCardMatrixVisible(): boolean {
    return (
      this.editMode ||
      this.collectDistinctCcCellsFromSnapshot().length > 0 ||
      this.manualInstallmentTiers.size > 0
    );
  }

  hasCashOutInDomain(): boolean {
    return !!this.fees?.domain?.some(
      f => f.operation === FlowDirection.CASH_OUT,
    );
  }

  getUnconfiguredIncomeMethods(): PaymentMethod[] {
    const domain = this.fees.domain ?? [];
    const configured = new Set(
      domain
        .filter(
          f => (f.operation ?? FlowDirection.INCOME) === FlowDirection.INCOME,
        )
        .map(f => f.paymentMethod),
    );
    return this.selectableIncomeMethods.filter(m => !configured.has(m));
  }

  canAddCashOut(): boolean {
    return this.scope === 'domain' && !this.hasCashOutInDomain();
  }

  addIncomePaymentMethod(method: PaymentMethod): void {
    this.enterEditModeFromAction();
    if (this.feesForm.contains(method)) {
      return;
    }
    this.feesForm.addControl(
      method,
      this.fb.group({
        type: ['amount', Validators.required],
        value: [
          0,
          [Validators.required, (c: FormControl) => this.validateFeeValue(c)],
        ],
        paymentMethod: [method],
        operation: [FlowDirection.INCOME],
      }),
    );
    this.syncTemplateLists();
  }

  addCashOutFee(): void {
    this.enterEditModeFromAction();
    if (this.feesForm.contains('cash_out')) {
      return;
    }
    this.feesForm.addControl(
      'cash_out',
      this.fb.group({
        type: ['amount', Validators.required],
        value: [
          0,
          [Validators.required, (c: FormControl) => this.validateFeeValue(c)],
        ],
        paymentMethod: [PaymentMethod.PIX],
        operation: [FlowDirection.CASH_OUT],
      }),
    );
    this.syncTemplateLists();
  }

  removeStandardFee(methodKey: string): void {
    if (this.feesForm.contains(methodKey)) {
      this.feesForm.removeControl(methodKey);
    }
    this.syncTemplateLists();
  }

  hasNoFees(): boolean {
    const noCc = this.collectDistinctCcCellsFromSnapshot().length === 0;
    const hasCashOut =
      this.fees?.domain?.some(f => f.operation === FlowDirection.CASH_OUT) ??
      false;
    if (this.scope === 'recipient') {
      return this.displayEditableFees.length === 0 && noCc;
    }
    const nonCcDomain =
      this.fees?.domain?.filter(f => f.paymentMethod !== 'credit_card') ?? [];
    return nonCcDomain.length === 0 && noCc && !hasCashOut;
  }

  loadDefaultFees(): void {
    if (!this.defaultFees || this.defaultFees.length === 0) {
      return;
    }
    this.handleSubmit(this.defaultFees);
  }

  shouldShowLoadDefaultFeesButton(): boolean {
    return (
      !this.editMode &&
      this.hasNoFees() &&
      this.defaultFees !== null &&
      this.defaultFees !== undefined &&
      this.defaultFees.length > 0
    );
  }

  getCcDomainType(tier: CcInstallmentTier): FeeType {
    const f =
      this.scope === 'domain'
        ? this.findDomainCcFeeExact(tier)
        : this.resolveRecipientCcDomainBase(tier);
    return f?.type || 'rate';
  }

  getCcDomainValue(tier: CcInstallmentTier): number {
    const f =
      this.scope === 'domain'
        ? this.findDomainCcFeeExact(tier)
        : this.resolveRecipientCcDomainBase(tier);
    return f ? this.getFeeValue(f) : 0;
  }

  getCcRecipientType(tier: CcInstallmentTier): FeeType {
    const f = this.findCcFeeInList(
      this.fees.recipient ?? [],
      tier,
      this.ccBrandless,
    );
    return f?.type || this.getCcDomainType(tier);
  }

  getCcRecipientValue(tier: CcInstallmentTier): number {
    const f = this.findCcFeeInList(
      this.fees.recipient ?? [],
      tier,
      this.ccBrandless,
    );
    return f ? this.getFeeValue(f) : 0;
  }

  getCcTotalDisplay(tier: CcInstallmentTier): string {
    const domainBase =
      this.scope === 'domain'
        ? this.findDomainCcFeeExact(tier)
        : this.resolveRecipientCcDomainBase(tier);
    const dType = domainBase?.type || 'rate';
    const dVal = domainBase ? this.getFeeValue(domainBase) : 0;
    if (this.scope === 'domain') {
      return this.formatFeeByType(dType, dVal);
    }
    const rType = this.getCcRecipientType(tier);
    const rVal = this.getCcRecipientValue(tier);
    if (dType === rType) {
      return this.formatFeeByType(dType, dVal + rVal);
    }
    return this.formatTotalWhenMixedTypes(dType, dVal, rType, rVal);
  }
}
